import type { Logger } from "@expert/logging";

import { removePii } from "@asurion-core/expert-workspace-pii";
import { sendEventMetric } from "@expert/cloudwatch-metrics";

export type SummaryComparisonStatus = "equal" | "different" | "gaia-only" | "simplr-only" | "both-empty";

/** Full comparison of the GAIA (live) and Simplr (shadow) summaries. Carries the raw summary text so
 *  a redacted before/after can be logged. Do NOT emit `gaiaValue`/`simplrValue` without scrubbing —
 *  use {@link SummaryComparisonResult} for PII-safe payloads. */
export interface SummaryComparison {
    status: SummaryComparisonStatus;
    gaiaLength: number;
    simplrLength: number;
    /** simplrLength - gaiaLength. */
    lengthDelta: number;
    /** Simplr shadow fetch duration in ms (GAIA is fetched on the live path before the shadow runs). */
    simplrLatencyMs?: number;
    gaiaValue: string;
    simplrValue: string;
}

/** PII-safe projection of a {@link SummaryComparison} — lengths only, never the summary text. */
export type SummaryComparisonResult = Omit<SummaryComparison, "gaiaValue" | "simplrValue">;

/** Classifies the GAIA vs Simplr summaries. Compared trimmed so trailing-whitespace-only differences
 *  do not register as a mismatch. */
export function compareSummaries(
    gaiaSummary: string,
    simplrSummary: string,
    simplrLatencyMs?: number,
): SummaryComparison {
    const gaia = gaiaSummary.trim();
    const simplr = simplrSummary.trim();

    let status: SummaryComparisonStatus;
    if (gaia === "" && simplr === "") {
        status = "both-empty";
    } else if (simplr === "") {
        status = "gaia-only";
    } else if (gaia === "") {
        status = "simplr-only";
    } else if (gaia === simplr) {
        status = "equal";
    } else {
        status = "different";
    }

    return {
        status,
        gaiaLength: gaia.length,
        simplrLength: simplr.length,
        lengthDelta: simplr.length - gaia.length,
        simplrLatencyMs,
        gaiaValue: gaiaSummary,
        simplrValue: simplrSummary,
    };
}

export function toSummaryComparisonResult(comparison: SummaryComparison): SummaryComparisonResult {
    return {
        status: comparison.status,
        gaiaLength: comparison.gaiaLength,
        simplrLength: comparison.simplrLength,
        lengthDelta: comparison.lengthDelta,
        simplrLatencyMs: comparison.simplrLatencyMs,
    };
}

/** CloudWatch metric name for a non-equal comparison status. */
export function metricNameForSummaryStatus(status: Exclude<SummaryComparisonStatus, "equal">): string {
    switch (status) {
        case "different":
            return "sessionSummarySimplr.mismatch";
        case "gaia-only":
            return "sessionSummarySimplr.gaiaOnly";
        case "simplr-only":
            return "sessionSummarySimplr.simplrOnly";
        case "both-empty":
            return "sessionSummarySimplr.bothEmpty";
    }
}

/**
 * Emits a GAIA-vs-Simplr summary comparison across three channels, matching the websocket shadow dark
 * launch:
 *  - **metric** — counts the non-equal status,
 *  - **log** — the summaries with PII scrubbed (only when they differ),
 *  - **analytic** — the PII-safe result (lengths only) via `onComparison`.
 *
 * Equal comparisons are logged at `debug` only (no metric, no analytic) so volume stays bounded.
 */
export function reportSummaryComparison(
    comparison: SummaryComparison,
    logger: Logger,
    onComparison?: (result: SummaryComparisonResult) => void,
): void {
    const result = toSummaryComparisonResult(comparison);

    if (comparison.status === "equal") {
        logger.debug(toShadowLogFields(result), "GAIA/Simplr summary shadow | summaries match");
        return;
    }

    void sendEventMetric(metricNameForSummaryStatus(comparison.status));
    onComparison?.(result);

    if (comparison.status === "different") {
        void logRedactedSummaries(comparison, logger);
    } else {
        logger.info(toShadowLogFields(result), `GAIA/Simplr summary shadow | ${comparison.status}`);
    }
}

/**
 * Log payload for a {@link SummaryComparisonResult}. The status goes under `shadowStatus` because `status` is a
 * reserved log field; `onComparison` still receives `status`.
 */
function toShadowLogFields(
    result: SummaryComparisonResult,
): Omit<SummaryComparisonResult, "status"> & { shadowStatus: SummaryComparisonStatus } {
    const { status, ...rest } = result;
    return { ...rest, shadowStatus: status };
}

async function logRedactedSummaries(comparison: SummaryComparison, logger: Logger): Promise<void> {
    try {
        const [gaia, simplr] = await Promise.all([removePii(comparison.gaiaValue), removePii(comparison.simplrValue)]);
        logger.warn(
            {
                shadowStatus: comparison.status,
                gaiaLength: comparison.gaiaLength,
                simplrLength: comparison.simplrLength,
                lengthDelta: comparison.lengthDelta,
                simplrLatencyMs: comparison.simplrLatencyMs,
                gaia,
                simplr,
            },
            "GAIA/Simplr summary shadow | summaries differ",
        );
    } catch (error: unknown) {
        logger.warn({ error }, "GAIA/Simplr summary shadow | failed to log redacted summaries");
    }
}
